import { FC, useCallback, useMemo } from "react";
import { useAppAbility } from "../../../../../../../../services/permissions";
import { useAppDispatch } from "../../../../../../../../store/hooks";
import { scheduleResourcesProtectionThunk } from "../../../../../../../../store/schedules/thunks/scheduleResourcesProtectionThunk";
import { CleanActionTypes } from "../../../../../../../../utils/clean-options";
import {
  ResourceFilters,
  ResourceType,
} from "../../../../../../../../services/cloudchipr.api";
import { ResourceItemType } from "../../../../../../../../store/account/utils/types/types";
import { PreviewDataGridActionComponent } from "../PreviewDataGridActionComponent";
import { protectorResourceProtectThunk } from "../../../../../../../../store/schedules/thunks/resources-for-protector/protectorResourceProtectThunk";

interface PreviewDataGridActionCellProps {
  resource: ResourceItemType;
  action: CleanActionTypes;
  accountIds: string[];
  resourceFilter?: ResourceFilters;
  regions?: string[];
  scheduleId: string;
  batchId?: string;
}

export const PreviewDataGridActionCell: FC<PreviewDataGridActionCellProps> = ({
  resource,
  action,
  accountIds,
  regions,
  resourceFilter,
  scheduleId,
  batchId,
}) => {
  const dispatch = useAppDispatch();
  const { cannot } = useAppAbility();
  const canNotProtectResource = cannot("protect", "resource");

  const resourceId = resource?.id;
  const accountId = resource?.account?.id;
  const isProtected = !!resource?.is_protected;
  const resourceType = resource?.resource_type as ResourceType;
  const viewIsForProtector = !resourceFilter && batchId;

  const resourcesToProtect = useMemo(() => {
    return {
      [accountId]: [
        {
          id: resourceId,
          type: resourceType,
          is_protected: !isProtected,
        },
      ],
    };
  }, [accountId, resourceId, resourceType, isProtected]);

  const scheduleResourcesProtectHandler = useCallback(async () => {
    if (!resourceFilter) {
      return;
    }

    await dispatch(
      scheduleResourcesProtectionThunk({
        scheduleId,
        accountIds,
        regions,
        resourcesToProtect,
        resourceFilters: [{ filter: resourceFilter }],
      }),
    );
  }, [
    resourcesToProtect,
    dispatch,
    scheduleId,
    resourceFilter,
    accountIds,
    regions,
  ]);

  const protectorResourcesProtectHandler = useCallback(async () => {
    if (!batchId) {
      return;
    }

    await dispatch(
      protectorResourceProtectThunk({
        scheduleId,
        batchId,
        resourcesToProtect,
      }),
    );
  }, [resourcesToProtect, dispatch, scheduleId, batchId]);

  const submitHandler = viewIsForProtector
    ? protectorResourcesProtectHandler
    : scheduleResourcesProtectHandler;

  return (
    <PreviewDataGridActionComponent
      action={action}
      batchId={batchId}
      resource={resource}
      scheduleId={scheduleId}
      onSubmit={submitHandler}
      protectDisabled={canNotProtectResource}
    />
  );
};
